import type { S3Client } from "@aws-sdk/client-s3";
import { GetObjectCommand, NoSuchKey, PutObjectCommand } from "@aws-sdk/client-s3";
import { getTermsJsonKey, RESOURCE_S3_BUCKET } from "@/config/terms";
import type { TermType, TermVersion } from "@/shared/types";

/**
 * S3からterms.jsonを取得
 */
export async function getTermsJson(s3Client: S3Client, type: TermType): Promise<TermVersion[]> {
	const key = getTermsJsonKey(type);

	try {
		const command = new GetObjectCommand({
			Bucket: RESOURCE_S3_BUCKET,
			Key: key,
		});
		const response = await s3Client.send(command);
		const bodyContents = await response.Body?.transformToString();
		if (bodyContents) {
			return JSON.parse(bodyContents);
		}
	} catch (error) {
		// terms.jsonが存在しない場合は空配列を返す
		if (!(error instanceof NoSuchKey)) {
			throw error;
		}
	}

	return [];
}

/**
 * S3にterms.jsonを保存
 */
export async function putTermsJson(
	s3Client: S3Client,
	type: TermType,
	versions: TermVersion[],
): Promise<void> {
	const key = getTermsJsonKey(type);

	const command = new PutObjectCommand({
		Bucket: RESOURCE_S3_BUCKET,
		Key: key,
		Body: JSON.stringify(versions, null, 2),
		ContentType: "application/json",
	});

	await s3Client.send(command);
}

/**
 * 新しいバージョンをterms.jsonに追加
 * @returns 更新後のバージョン配列
 * @throws バージョンが既に存在する場合
 */
export async function addTermVersion(
	s3Client: S3Client,
	type: TermType,
	newVersion: TermVersion,
): Promise<TermVersion[]> {
	const existingVersions = await getTermsJson(s3Client, type);

	// 同じバージョンが既に存在するかチェック
	const versionExists = existingVersions.some((v) => v.version === newVersion.version);
	if (versionExists) {
		throw new Error(`Version ${newVersion.version} already exists`);
	}

	// 新しいバージョンを先頭に追加
	const updatedVersions = [newVersion, ...existingVersions];

	await putTermsJson(s3Client, type, updatedVersions);

	return updatedVersions;
}
